import type { Lang } from "../content/types";
import type { Goal } from "../content/copy";
import type { DailyCardId } from "../content/dailycards";

export const PROFILE_KEY = "dictionDojo.v2";

export type ThemeSetting = "system" | "dark" | "light";
export type TimeOfDay = "morning" | "midday" | "evening";
export type SessionLength = "short" | "standard" | "long";
export type Grade = 0 | 1 | 2 | 3; // Again, Hard, Good, Easy

export type SrsCard = {
  id: string;
  due: number; // epoch day index
  interval: number; // days
  ease: number; // 1.30 .. 3.00
  reps: number;
  lapses: number;
  lastGrade: Grade;
  bestWpm?: number;
  /** day this card first crossed the mastery threshold (see engine/mastery.ts) */
  masteredAt?: string;
};

export type RingDay = {
  closed: boolean;
  items: number;
  seconds: number;
  clarity: number;
  langs: Lang[];
};

export type PhonemeProgress = { xp: number; attempts: number; clean: number };

export type SessionLogEntry = {
  at: string; // ISO timestamp
  clarity: number;
  seconds: number;
  itemsCleared: number;
  waveform: number[]; // downsampled amplitude samples (~60 floats)
  note?: string;
  dailyCard?: DailyCardId | null;
};

export type StreakState = {
  current: number;
  longest: number;
  lastDay: string | null;
  restDays: number; // banked, 0..2
  consecutiveClosedRings: number;
  brokenAt: { day: string; lostStreak: number } | null;
  repairLastUsed: string | null;
  pausedUntil: string | null;
};

export type Profile = {
  v: 2;
  createdAt: string;
  seed: number;
  identity: {
    goal: Goal | null;
    langs: Lang[];
    primaryLang: Lang;
    when: TimeOfDay | null;
  };
  clarity: number;
  seconds: number;
  sessions: number;
  streak: StreakState;
  week: { target: 3 | 5 | 7 };
  rings: Record<string, RingDay>;
  cards: Record<string, SrsCard>;
  phonemes: Record<string, PhonemeProgress>;
  bests: {
    phonationSec: number;
    breathCount: number;
    plosiveAccuracy: number;
    steadinessPct: number;
    wpmByCard: Record<string, number>;
    bestWeek: number;
    dayMax: number;
  };
  plaques: Record<string, { at: string }>;
  log: SessionLogEntry[];
  settings: {
    theme: ThemeSetting;
    sound: { master: boolean; metronome: boolean; sfx: boolean; voice: boolean };
    mic: boolean;
    transcribeCoach: boolean;
    newPerDay: number;
    reviewCap: number;
    sessionLength: SessionLength;
  };
  seen: { onboarded: boolean; tips: string[] };
};

export function defaultProfile(seed = Math.floor(Math.random() * 1_000_000_000)): Profile {
  return {
    v: 2,
    createdAt: new Date().toISOString(),
    seed,
    identity: { goal: null, langs: ["en"], primaryLang: "en", when: null },
    clarity: 0,
    seconds: 0,
    sessions: 0,
    streak: {
      current: 0,
      longest: 0,
      lastDay: null,
      restDays: 0,
      consecutiveClosedRings: 0,
      brokenAt: null,
      repairLastUsed: null,
      pausedUntil: null,
    },
    week: { target: 5 },
    rings: {},
    cards: {},
    phonemes: {},
    bests: {
      phonationSec: 0,
      breathCount: 0,
      plosiveAccuracy: 0,
      steadinessPct: 0,
      wpmByCard: {},
      bestWeek: 0,
      dayMax: 0,
    },
    plaques: {},
    log: [],
    settings: {
      theme: "system",
      sound: { master: true, metronome: true, sfx: true, voice: false },
      mic: false,
      transcribeCoach: false,
      newPerDay: 2,
      reviewCap: 10,
      sessionLength: "standard",
    },
    seen: { onboarded: false, tips: [] },
  };
}

export function emptyRingDay(): RingDay {
  return { closed: false, items: 0, seconds: 0, clarity: 0, langs: [] };
}

/** Loads from localStorage under PROFILE_KEY. No v1 migration — a stale
    dictionDojo.v1 blob (the old vanilla-JS app) is simply ignored. */
export function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return defaultProfile();
    const parsed = JSON.parse(raw);
    if (parsed?.v !== 2) return defaultProfile();
    // shallow-merge over defaults so new fields introduced later don't crash old saves
    const base = defaultProfile(parsed.seed);
    return {
      ...base,
      ...parsed,
      identity: { ...base.identity, ...parsed.identity },
      streak: { ...base.streak, ...parsed.streak },
      week: { ...base.week, ...parsed.week },
      bests: { ...base.bests, ...parsed.bests },
      settings: {
        ...base.settings,
        ...parsed.settings,
        sound: { ...base.settings.sound, ...parsed.settings?.sound },
      },
      seen: { ...base.seen, ...parsed.seen },
    };
  } catch {
    return defaultProfile();
  }
}

export function saveProfile(profile: Profile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
  } catch {
    /* storage full/blocked — play on without persistence */
  }
}
